import React from 'react';
import clipboardCross from 'assets/clipboardCross.svg';
import AnimatedToggle from 'components/Modals/AnimatedToggle.tsx/AnimatedToggle';
import Search from 'components/Organisms/Search';
import EmptyTable from 'components/Table/EmptyTable';
import { components } from 'generated/payment-initialisation/schema';
import Authorization from 'hocs/Authorization';
import useAuthorization from 'hooks/useAuthorization';
import useSwitchAccount from 'hooks/useSwitchAccount';
import { UserRole } from 'models/user';
import t from 'utils/translationHelper';

import { ActionButton } from '@alpha/ui-lib/ui/button';
import { Loader } from '@alpha/ui-lib/ui/Loader';
import APPagination from '@alpha/ui-lib/ui/Pagination/APPagination/APPagination';
import { StyledGenericTable } from '@alpha/ui-lib/ui/table/StyledGenericTable';
import { faPlusCircle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { remittersColumns, remittersDataMapper } from './RemittersColumns';
import useStyles from './RemittersTable.styles';

interface IRemittersTableProps {
    data: components['schemas']['RemittersListResponse'];
    setOpenCreateRemitterDrawer: (open: boolean) => void;
    setEditRemitterId: (id?: string) => void;
    isLoading: boolean;
    activeTab: string;
    setActiveTab: (tab: string) => void;
    setSearchQuery: (query: string) => void;
    searchQuery: string;
    setPageNumber: (prevPageNumber: (prevPageNumber: number) => number) => void;
    pageNumber: number;
}

const RemittersTable: React.FC<IRemittersTableProps> = ({
  data, setOpenCreateRemitterDrawer, setEditRemitterId, isLoading, activeTab, setActiveTab, setSearchQuery, searchQuery, setPageNumber, pageNumber,
}: IRemittersTableProps) => {
  const classes = useStyles();

  const handleEditRemitter = (id?: string) => {
    setEditRemitterId(id);
    setOpenCreateRemitterDrawer(true);
  };

  const requiredRoles = [[UserRole.BENEFICAIRY_APPROVER_OWN,
    UserRole.BENEFICIARY_INPUTTER,
    UserRole.BENEFICIARY_APPROVER,
    UserRole.PAYMENTS_APPROVER,
    UserRole.PAYMENTS_INPUTTER,
    UserRole.PAYMENTS_APPROVER_OWN,
    UserRole.PAYMENT_FILE_INPUTTER]];

  const { authorized } = useAuthorization(requiredRoles);

  return (
    <div className={classes.tableWrapper}>
      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '24px 24px 0px 24px' }}>
        <AnimatedToggle
          options={[
            { value: 'actionRequired', label: t('action_required') },
            { value: 'allRemitters', label: t('all_remitters') },
          ]}
          value={activeTab}
          onChange={setActiveTab}
        />
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <Search
            testId="remitter-name"
            value={searchQuery}
            placeholder={t('search_remitter_name_and_bank_name')}
            onChange={(e) => setSearchQuery(e.target.value)}
            hideVerticalDivider
          />
          <Authorization requiredRoles={requiredRoles}>
            <ActionButton
              style={{ width: '138px' }}
              onClick={() => {
                setOpenCreateRemitterDrawer(true);
              }}
            >
              <FontAwesomeIcon icon={faPlusCircle} style={{ marginRight: '8px' }} />
              {t('create_remitter')}
            </ActionButton>
          </Authorization>
        </div>
      </div>
      {isLoading ? (
        <div style={{ height: '654px' }}>
          <Loader testId="remitters-table-loading" />
        </div>
      ) : (
        <>
          {(data?.Remitters?.length ?? 0) > 0 ? (
            <>
              <StyledGenericTable
                columns={authorized ? remittersColumns : remittersColumns.filter((column) => column.accessor !== 'Actions')}
                data={remittersDataMapper(data.Remitters, handleEditRemitter) || []}
                testId="remitters-table"
                className={classes.table}
              />
              <div className={classes.paginationWrapper}>
                <div style={{ fontWeight: 400, fontSize: '10px', color: '#999999' }}>
                  {t('showing')}
                  {' '}
                  {(pageNumber * 10) + 1}
                  {' - '}
                  {(pageNumber * 10) + (data?.Remitters?.length ?? 0)}
                  {' '}
                  {t('of')}
                  {' '}
                  {data?.TotalRecords}
                </div>
                <div>
                  <APPagination
                    hasNext={data.IsLastPage === false}
                    hasPrevious={pageNumber > 0}
                    handleNext={() => {
                      setPageNumber((prevPageNumber: number) => prevPageNumber + 1);
                    }}
                    handlePrevious={() => {
                      setPageNumber((prevPageNumber: number) => prevPageNumber - 1);
                    }}
                    nextString={t('next^')}
                    prevString={t('prev^')}
                  />
                </div>
              </div>
            </>
          ) : (
            <EmptyTable
              icon={clipboardCross}
              title={t('no_remitters')}
              className={classes.emptyTable}
            />
          )}
        </>
      )}
    </div>
  );
};

export default RemittersTable;
